/** biome-ignore-all lint/suspicious/noDoubleEquals: <3rd-party library> */
import { memo, type RefObject, useEffect, useRef } from 'react';

import { usePDFCurrentSearchResultElementContext } from '../contexts/PDFCurrentSearchResultElementContext';
import { usePDFViewerSearchContext } from '../contexts/PDFViewerSearch/PDFViewerSearchContext';
import { type PDFVirtualizedListHandle } from './PDFPagesVirtualizedList';

type ScrollToCurrentPDFResultObserverProps = {
  listRef: RefObject<PDFVirtualizedListHandle>;
  currentPageNumber: number;
};

/**
 * This component observes the current search result index and scrolls to the current search result element
 */
export const ScrollToCurrentPDFResultObserver = memo(
  ({ listRef, currentPageNumber }: ScrollToCurrentPDFResultObserverProps) => {
    const { currentResultIndex, getMatchesMapArray } = usePDFViewerSearchContext();
    const { subscribeToCurrentSearchResultElement, getCurrentSearchResultElement } =
      usePDFCurrentSearchResultElementContext();

    const currentPageNumberRef = useRef(currentPageNumber);
    currentPageNumberRef.current = currentPageNumber;

    useEffect(() => {
      const subscription = subscribeToCurrentSearchResultElement((element) => {
        // when page is mounted and element is available, scroll to it
        requestAnimationFrame(() => {
          element?.element?.scrollIntoView({ block: 'center', inline: 'center' });
        });
      });

      return () => {
        subscription.unsubscribe();
      };
    }, [subscribeToCurrentSearchResultElement]);

    useEffect(() => {
      const matchesMapArray = getMatchesMapArray();
      const selectedMatch = matchesMapArray[currentResultIndex];
      const pageIndex = selectedMatch?.pageIndex ?? -1;
      const element = getCurrentSearchResultElement();
      if (element) {
        element?.element?.scrollIntoView({ block: 'center', inline: 'center' });
      } else if (pageIndex != -1 && pageIndex !== currentPageNumberRef.current - 1) {
        listRef.current?.scrollToIndex(pageIndex);
      }
    }, [currentResultIndex, getCurrentSearchResultElement, getMatchesMapArray, listRef]);

    return null;
  },
);

ScrollToCurrentPDFResultObserver.displayName = 'ScrollToCurrentPDFResultObserver';
